import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { GAME_MODES, GAME_SKINS } from '../data/siteContent';
import { LocalProfile, ScoreEntry, SignUpInput, SitePreferences } from '../types/site';
import { readStorage, STORAGE_KEYS, writeStorage } from '../utils/storage';

interface ScoreInput {
  modeId: string;
  skinId: string;
  score: number;
  durationMs: number;
  nickname?: string;
}

interface SiteContextValue {
  profile: LocalProfile | null;
  isSignedIn: boolean;
  preferences: SitePreferences;
  scores: ScoreEntry[];
  topScore: number;
  signUp: (input: SignUpInput) => void;
  signIn: (email: string) => boolean;
  signOut: () => void;
  updateProfile: (updates: Partial<SignUpInput>) => void;
  updatePreferences: (updates: Partial<SitePreferences>) => void;
  recordScore: (input: ScoreInput) => void;
}

const defaultPreferences: SitePreferences = {
  activeModeId: GAME_MODES[0].id,
  activeSkinId: GAME_SKINS[0].id
};

const SiteContext = createContext<SiteContextValue | undefined>(undefined);

function sortScores(entries: ScoreEntry[]): ScoreEntry[] {
  return [...entries].sort((left, right) => {
    if (right.score !== left.score) {
      return right.score - left.score;
    }

    return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
  });
}

export const SiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState<LocalProfile | null>(() =>
    readStorage<LocalProfile | null>(STORAGE_KEYS.profile, null)
  );
  const [isSignedIn, setIsSignedIn] = useState<boolean>(() =>
    readStorage<boolean>(STORAGE_KEYS.session, false)
  );
  const [preferences, setPreferences] = useState<SitePreferences>(() =>
    readStorage<SitePreferences>(STORAGE_KEYS.preferences, defaultPreferences)
  );
  const [scores, setScores] = useState<ScoreEntry[]>(() =>
    readStorage<ScoreEntry[]>(STORAGE_KEYS.leaderboard, [])
  );

  useEffect(() => {
    writeStorage(STORAGE_KEYS.profile, profile);
  }, [profile]);

  useEffect(() => {
    writeStorage(STORAGE_KEYS.session, isSignedIn);
  }, [isSignedIn]);

  useEffect(() => {
    writeStorage(STORAGE_KEYS.preferences, preferences);
  }, [preferences]);

  useEffect(() => {
    writeStorage(STORAGE_KEYS.leaderboard, scores);
  }, [scores]);

  const signUp = (input: SignUpInput): void => {
    const nextProfile: LocalProfile = {
      nickname: input.nickname.trim(),
      email: input.email.trim().toLowerCase(),
      tagline: input.tagline.trim(),
      favoriteModeId: input.favoriteModeId,
      favoriteSkinId: input.favoriteSkinId,
      createdAt: profile?.createdAt ?? new Date().toISOString()
    };

    setProfile(nextProfile);
    setIsSignedIn(true);
    setPreferences({
      activeModeId: nextProfile.favoriteModeId,
      activeSkinId: nextProfile.favoriteSkinId
    });
  };

  const signIn = (email: string): boolean => {
    const normalizedEmail = email.trim().toLowerCase();

    if (!profile || profile.email !== normalizedEmail) {
      return false;
    }

    setIsSignedIn(true);
    return true;
  };

  const signOut = (): void => {
    setIsSignedIn(false);
  };

  const updateProfile = (updates: Partial<SignUpInput>): void => {
    setProfile((currentProfile) => {
      if (!currentProfile) {
        return currentProfile;
      }

      const nextProfile: LocalProfile = {
        ...currentProfile,
        nickname: updates.nickname?.trim() || currentProfile.nickname,
        email: updates.email?.trim().toLowerCase() || currentProfile.email,
        tagline: updates.tagline?.trim() || currentProfile.tagline,
        favoriteModeId: updates.favoriteModeId || currentProfile.favoriteModeId,
        favoriteSkinId: updates.favoriteSkinId || currentProfile.favoriteSkinId
      };

      if (updates.favoriteModeId || updates.favoriteSkinId) {
        setPreferences((currentPreferences) => ({
          activeModeId: updates.favoriteModeId || currentPreferences.activeModeId,
          activeSkinId: updates.favoriteSkinId || currentPreferences.activeSkinId
        }));
      }

      return nextProfile;
    });
  };

  const updatePreferences = (updates: Partial<SitePreferences>): void => {
    setPreferences((currentPreferences) => ({
      ...currentPreferences,
      ...updates
    }));
  };

  const recordScore = (input: ScoreInput): void => {
    if (input.score <= 0) {
      return;
    }

    const entry: ScoreEntry = {
      id: `${Date.now()}-${Math.round(Math.random() * 1_000_000)}`,
      nickname: input.nickname?.trim() || profile?.nickname || 'Guest',
      modeId: input.modeId,
      skinId: input.skinId,
      score: input.score,
      createdAt: new Date().toISOString(),
      durationMs: input.durationMs
    };

    setScores((currentScores) => sortScores([entry, ...currentScores]).slice(0, 60));
  };

  const topScore = useMemo(() => {
    return scores[0]?.score ?? 0;
  }, [scores]);

  const value = useMemo<SiteContextValue>(
    () => ({
      profile,
      isSignedIn,
      preferences,
      scores,
      topScore,
      signUp,
      signIn,
      signOut,
      updateProfile,
      updatePreferences,
      recordScore
    }),
    [profile, isSignedIn, preferences, scores, topScore]
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export function useSiteContext(): SiteContextValue {
  const context = useContext(SiteContext);

  if (!context) {
    throw new Error('useSiteContext must be used within SiteProvider');
  }

  return context;
}
